import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface PatternDetection {
  id: string;
  flow_id: string;
  confidence: number;
  evidence_text: string;
  affected_elements: string[];
  detected_at: string;
  flow: {
    id: string;
    master_label: string;
    developer_name: string;
    sf_org_id: string;
  };
}

interface Props {
  patternKey: string;
  patternName: string;
  onClose: () => void;
}

export default function PatternFlowExamples({ patternKey, patternName, onClose }: Props) {
  const [detections, setDetections] = useState<PatternDetection[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDetections();
  }, [patternKey]);

  const loadDetections = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('pattern_detections')
        .select(`
          id,
          flow_id,
          confidence,
          evidence_text,
          affected_elements,
          detected_at,
          flow:sf_flow_definitions!inner(
            id,
            master_label,
            developer_name,
            sf_org_id
          )
        `)
        .eq('pattern_key', patternKey)
        .order('confidence', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Type assertion needed because Supabase nested select typing
      setDetections(data as any || []);
    } catch (error) {
      console.error('Error loading detections:', error);
      alert('Failed to load flow examples');
    } finally {
      setLoading(false);
    }
  };

  const viewFlow = (flowId: string, orgId: string) => {
    navigate(`/flows/${flowId}?orgId=${orgId}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{patternName}</h2>
            <p className="text-gray-600 mt-1">
              {detections.length} flow{detections.length !== 1 ? 's' : ''} with this pattern
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : detections.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No flow examples found
            </div>
          ) : (
            <div className="space-y-4">
              {detections.map((detection) => (
                <div
                  key={detection.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {detection.flow.master_label}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {detection.flow.developer_name}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {(detection.confidence * 100).toFixed(0)}% confidence
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(detection.detected_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => viewFlow(detection.flow.id, detection.flow.sf_org_id)}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium text-sm flex items-center space-x-1"
                      >
                        <span>View Flow</span>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Evidence */}
                  {detection.evidence_text && (
                    <div className="bg-gray-50 rounded p-3 mb-3">
                      <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Evidence
                      </div>
                      <p className="text-sm text-gray-700">
                        {detection.evidence_text}
                      </p>
                    </div>
                  )}

                  {/* Affected Elements */}
                  {detection.affected_elements && detection.affected_elements.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                        Affected Elements
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {detection.affected_elements.map((element, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono"
                          >
                            {element}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
